// src/features/fx-info/components/FxSeasonalityHeatmap.tsx
//
// Pillar 2 — Calendar Seasonality Heatmap.
// A purely CSS-driven responsive grid (no charting library). Each cell shows a
// calendar month's average historical return and its win-rate (positivity),
// colour-graded green (positive flow) to red (negative flow). Because it is a
// native CSS Grid, it adapts fluidly across mobile / tablet / desktop
// breakpoints while conveying positive/negative flow cycles at a glance.

import { useRef } from 'react';

import type { FxSeasonalityMonth } from '../api';
import { ChartDownloadMenu } from '../../../shared/ui';
import { useChartExport } from '../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../shared/utils/chartFilename';

interface Props {
    data: FxSeasonalityMonth[];
}

export const FxSeasonalityHeatmap = ({ data }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    const formatPct = (val: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
            signDisplay: 'always',
        }).format(val);

    const getBgColor = (avgReturn: number) => {
        if (avgReturn > 0.01) return 'bg-emerald-500/20 border-emerald-500/30';
        if (avgReturn > 0.002) return 'bg-emerald-500/10 border-emerald-500/20';
        if (avgReturn >= 0) return 'bg-emerald-500/5 border-emerald-500/10';
        if (avgReturn < -0.01) return 'bg-red-500/20 border-red-500/30';
        if (avgReturn < -0.002) return 'bg-red-500/10 border-red-500/20';
        return 'bg-red-500/5 border-red-500/10';
    };

    if (!data.length) {
        return (
            <div className="w-full h-full flex flex-col min-h-[350px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">
                No seasonality data available.
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col min-h-[350px]">
            <div className="flex items-start justify-between gap-4 mb-1">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Calendar Seasonality Heatmap
                </h2>
                <ChartDownloadMenu
                    label="Calendar Seasonality Heatmap"
                    onPng={() => downloadPng({ filename: buildChartFilename('fx-seasonality-heatmap') })}
                    onJpeg={() => downloadJpeg({ filename: buildChartFilename('fx-seasonality-heatmap') })}
                />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                Average monthly return and historical win-rate (positivity).
            </p>

            <div
                ref={containerRef}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 flex-1 place-content-center bg-white dark:bg-slate-900"
            >
                {data.map((m) => (
                    <div
                        key={m.month}
                        className={`flex flex-col items-center justify-center p-4 rounded-xl border ${getBgColor(
                            m.average_return,
                        )} transition-colors`}
                    >
                        <span className="text-slate-600 dark:text-slate-300 font-semibold mb-1 uppercase tracking-wider text-xs">
                            {m.month_label}
                        </span>
                        <span
                            className={`text-lg font-bold font-mono mb-2 ${
                                m.average_return >= 0
                                    ? 'text-emerald-700 dark:text-emerald-400'
                                    : 'text-red-700 dark:text-red-400'
                            }`}
                        >
                            {formatPct(m.average_return)}
                        </span>
                        <div className="flex items-center gap-2 w-full pt-2 border-t border-slate-900/10 dark:border-white/10 justify-between">
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                Win Rate
                            </span>
                            <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300">
                                {(m.win_rate * 100).toFixed(0)}%
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FxSeasonalityHeatmap;
